'use client'

import { Fragment, useMemo } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/utils/styles/cn'

export interface ISplitHeadingProps {
    text: string
    className?: string
    secondLineClassName?: string
    /** If false, renders without motion. */
    animate?: boolean
    /** Base delay for per-line stagger. */
    lineStaggerDelay?: number
    /** Y offset for line entrance. */
    lineInitialY?: number
}

function splitIntoLines(text: string) {
    const rawLines = text
        .split(/[\r\n\u2028\u2029]+/)
        .map((l) => l.trim())
        .filter(Boolean)

    if (rawLines.length > 1) return rawLines

    // Only split when the text explicitly contains a line break.
    // We don't try to "smart split" here because it can create awkward wrapping
    // (e.g. forcing 3-line headings when container width changes).
    return [text.replace(/\s+/g, ' ').trim()]
}

export function SplitHeading({
    text,
    className,
    secondLineClassName,
    animate = true,
    lineStaggerDelay = 0.08,
    lineInitialY = 16,
}: ISplitHeadingProps) {
    const lines = useMemo(() => splitIntoLines(text), [text])

    return (
        <span className={cn('block leading-[0.9]', className)}>
            {lines.map((line, i) => (
                <Fragment key={`${i}-${line}`}>
                    {i > 0 && <br />}
                    {animate ? (
                        <motion.span
                            initial={{ opacity: 0, y: lineInitialY }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                delay: lineStaggerDelay * i,
                                type: 'spring',
                                stiffness: 220,
                                damping: 24,
                            }}
                            style={{ display: 'inline-block', width: '100%' }}
                            className={cn(i > 0 && (secondLineClassName ?? 'ds-heading-muted'))}
                        >
                            {line}
                        </motion.span>
                    ) : (
                        <span className={cn(i > 0 && (secondLineClassName ?? 'ds-heading-muted'))}>{line}</span>
                    )}
                </Fragment>
            ))}
        </span>
    )
}
